/**
 * What the cart must tell the customer before checkout: a line that can no
 * longer be bought, a quantity above what is left, and a price that changed
 * since they last saw it. Every figure here is the server's — the server
 * re-prices cart lines live (orders/models/cart.py, CartItem.unit_price) and
 * checkout re-checks availability and stock (payments/views/charges.py).
 */

const num = v => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** One line's problem, or null: { kind: 'unavailable' } | { kind: 'low_stock', left }. */
export function lineIssue(item) {
  const product = item?.product
  if (!product) return null
  if (product.is_available === false || (product.status && product.status !== 'active')) return { kind: 'unavailable' }
  const qty = num(item.quantity) ?? 0
  // A variant has its own stock; otherwise the product's. (A grocery line may
  // be supplied by a linked seller with more, so this warns, it does not block.)
  const left = item.variant ? num(item.variant.inventory_quantity) : num(product.stock_quantity)
  if (left !== null && qty > left) return { kind: 'low_stock', left: Math.max(0, left) }
  return null
}

/** A cart line's identity across refreshes: the product and its variant. */
export const priceKey = item => `${item?.product?.id ?? ''}:${item?.variant?.id ?? item?.variant_id ?? ''}`

const isServerLine = item => item && !item.isOptimistic && !item.isOptimisticUpdate && !(typeof item.id === 'string' && item.id.startsWith('optimistic'))

/**
 * Compare the server's unit prices with the ones the customer last saw.
 * Returns the changes and the updated "seen" map. A line seen for the first
 * time is only recorded; lines no longer in the cart are forgotten.
 */
export function diffSeenPrices(seen = {}, items = []) {
  const next = {}
  const changes = []
  for (const item of items) {
    if (!isServerLine(item)) {
      const key = priceKey(item)
      if (key in seen) next[key] = seen[key]
      continue
    }
    const key = priceKey(item)
    const price = num(item.unit_price)
    if (price === null) continue
    const before = num(seen[key])
    if (before !== null && Math.round(before) !== Math.round(price)) {
      changes.push({ key, name: item.product?.name || 'An item', variant: item.variant ? `${item.variant.name}: ${item.variant.value}` : null, from: before, to: price })
    }
    next[key] = price
  }
  return { changes, seen: next }
}

/** Merge new changes into the pending list (one entry per line, first "from" kept). */
export function mergePriceChanges(pending = [], incoming = []) {
  const byKey = new Map(pending.map(c => [c.key, c]))
  for (const c of incoming) {
    const prior = byKey.get(c.key)
    byKey.set(c.key, prior ? { ...c, from: prior.from } : c)
  }
  // A line that went back to the price first seen is no change at all.
  return [...byKey.values()].filter(c => Math.round(c.from) !== Math.round(c.to))
}
